import { useState } from "react";
import { Avatar, Box } from "@mui/material";
import dayjs from "dayjs";
import Flex from "@/components/Flex";
import LoadingOverlay from "@/components/LoadingOverlay";
import SideSheetDrawer from "@/components/SidesheetDrawer";
import Text from "@/components/Text";
import StyledButtonV2 from "@/components/app/StyledButtonV2";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import { getInitials } from "@/utils/utils";
import {
  APPOINTMENT_DETAIL_SEPARATOR,
  APPOINTMENT_DRAWER_LABEL,
  APPOINTMENT_DRAWER_TIME_FORMAT,
  APPOINTMENT_DRAWER_WIDTH,
  GROUP_APPOINTMENT_STATUS,
} from "../group-appointment-constant";
import { GroupSessionDetail } from "../group-appointment-types";
import GroupSessionPatientRow from "./GroupSessionPatientRow";

export type GroupAppointmentDrawerProps = {
  opened: boolean;
  onClose: () => void;
  detail?: GroupSessionDetail;
  isLoading?: boolean;
  onCancel: () => void;
  onReschedule: () => void;
  /** Same role gate as the individual drawer's Reschedule Appointment action. */
  canReschedule: boolean;
  onCompleteCheckIn: () => void;
  onCompleteVisit: () => void;
  onViewSummary: () => void;
};

const AVATAR_SIZE = 34;
const DETAIL_LABEL_WIDTH = 120;

/**
 * Group session at a glance with the status-driven actions of the individual
 * appointment drawer: cancel / check in while scheduled, complete the visit
 * while in exam or doc pending, review once completed.
 *
 * @author Rajkumar Rathod
 */
const GroupAppointmentDrawer = ({
  opened,
  onClose,
  detail,
  isLoading,
  onCancel,
  onReschedule,
  canReschedule,
  onCompleteCheckIn,
  onCompleteVisit,
  onViewSummary,
}: GroupAppointmentDrawerProps) => {
  const [openFormsId, setOpenFormsId] = useState<string>();
  const toggleForms = (appointmentId: string) =>
    setOpenFormsId((current) =>
      current === appointmentId ? undefined : appointmentId
    );
  const status = detail?.sessionStatus;
  const isScheduled = status === GROUP_APPOINTMENT_STATUS.SCHEDULED;
  const isInExam = status === GROUP_APPOINTMENT_STATUS.IN_EXAM;
  const isInProgress = status === GROUP_APPOINTMENT_STATUS.DOC_PENDING;
  const isCompleted = status === GROUP_APPOINTMENT_STATUS.COMPLETED;

  const renderDetail = (label: string, value?: string | null) => (
    <Flex alignItems="center" gap={1}>
      <Text
        {...CHART_TYPOGRAPHY.body5}
        c={CHART_COLORS.textMutedGrey}
        sx={{ width: DETAIL_LABEL_WIDTH, flexShrink: 0 }}
      >
        {label}
      </Text>
      <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textMutedGrey}>
        {APPOINTMENT_DETAIL_SEPARATOR}
      </Text>
      <Text {...CHART_TYPOGRAPHY.body4} c={CHART_COLORS.textDark}>
        {value || "-"}
      </Text>
    </Flex>
  );

  const renderSectionTitle = (title: string) => (
    <Box
      sx={{
        px: 2,
        py: 1,
        backgroundColor: CHART_COLORS.surfaceSubtle,
        borderTop: `1px solid ${CHART_COLORS.borderLight}`,
        borderBottom: `1px solid ${CHART_COLORS.borderLight}`,
      }}
    >
      <Text
        {...CHART_TYPOGRAPHY.body5}
        fontWeight={CHART_TYPOGRAPHY.weight.medium}
        c={CHART_COLORS.textDarker}
      >
        {title}
      </Text>
    </Box>
  );

  return (
    <SideSheetDrawer
      open={opened}
      onClose={onClose}
      title={
        isScheduled
          ? APPOINTMENT_DRAWER_LABEL.checkInTitle
          : APPOINTMENT_DRAWER_LABEL.title
      }
      w={APPOINTMENT_DRAWER_WIDTH}
      withCloseIcon
      footerActions={
        <Flex alignItems="center" gap={1} justifyContent="flex-end">
          {isScheduled && (
            <>
              <StyledButtonV2
                label={APPOINTMENT_DRAWER_LABEL.cancelAppointment}
                variant="text"
                color="error"
                onClick={onCancel}
              />
              {canReschedule && (
                <StyledButtonV2
                  label={APPOINTMENT_DRAWER_LABEL.rescheduleAppointment}
                  variant="outlined"
                  onClick={onReschedule}
                />
              )}
            </>
          )}
          {(isScheduled || isInExam) && (
            <StyledButtonV2
              label={APPOINTMENT_DRAWER_LABEL.completeCheckIn}
              variant="primary"
              onClick={onCompleteCheckIn}
            />
          )}
          {isInProgress && (
            <StyledButtonV2
              label={APPOINTMENT_DRAWER_LABEL.completeVisit}
              variant="primary"
              onClick={onCompleteVisit}
            />
          )}
          {isCompleted && (
            <StyledButtonV2
              label={APPOINTMENT_DRAWER_LABEL.sessionSummary}
              variant="primary"
              onClick={onViewSummary}
            />
          )}
        </Flex>
      }
    >
      <LoadingOverlay isLoading={!!isLoading} />

      <Flex alignItems="center" gap={1.5} sx={{ px: 2, py: 1.5 }}>
        <Avatar
          sx={{
            width: AVATAR_SIZE,
            height: AVATAR_SIZE,
            fontSize: CHART_TYPOGRAPHY.body5.fontSize.lg,
            bgcolor: CHART_COLORS.surfaceAlt,
            color: CHART_COLORS.textDark,
          }}
        >
          {getInitials(detail?.groupName ?? "")}
        </Avatar>

        <Text
          {...CHART_TYPOGRAPHY.body3}
          fontWeight={CHART_TYPOGRAPHY.weight.medium}
          c={CHART_COLORS.textDarker}
        >
          {detail?.groupName}
        </Text>
      </Flex>

      {renderSectionTitle(APPOINTMENT_DRAWER_LABEL.appointmentDetails)}

      <Flex flexDirection="column" gap={1} sx={{ px: 2, py: 1.5 }}>
        {renderDetail(APPOINTMENT_DRAWER_LABEL.location, detail?.locationName)}
        {renderDetail(
          APPOINTMENT_DRAWER_LABEL.scheduledTime,
          detail?.startTime
            ? dayjs(detail.startTime).format(APPOINTMENT_DRAWER_TIME_FORMAT)
            : undefined
        )}
        {renderDetail(
          APPOINTMENT_DRAWER_LABEL.therapist,
          detail?.therapistName
        )}
        {renderDetail(
          APPOINTMENT_DRAWER_LABEL.coFacilitator,
          detail?.coTherapistName
        )}
        {renderDetail(
          APPOINTMENT_DRAWER_LABEL.enrolled,
          detail
            ? `${detail.enrolledPatients ?? 0} ${APPOINTMENT_DRAWER_LABEL.enrolledSuffix}`
            : undefined
        )}
      </Flex>

      {renderSectionTitle(APPOINTMENT_DRAWER_LABEL.patientDetails)}

      <Flex flexDirection="column">
        {detail?.patients?.length
          ? detail.patients.map((patient) => (
              <GroupSessionPatientRow
                key={patient.appointmentId}
                patient={patient}
                isFormsOpen={openFormsId === patient.appointmentId}
                onToggleForms={toggleForms}
              />
            ))
          : !isLoading && (
              <Text
                {...CHART_TYPOGRAPHY.body5}
                c={CHART_COLORS.textMutedGrey}
                sx={{ px: 2, py: 1.5 }}
              >
                {APPOINTMENT_DRAWER_LABEL.noPatients}
              </Text>
            )}
      </Flex>
    </SideSheetDrawer>
  );
};

export default GroupAppointmentDrawer;
